import {useCallback, useEffect, useState} from 'react';

const LOG_PREFIX = '[useCarver]';
const BASE = (process.env.REACT_APP_AWP_API_BASE || process.env.REACT_APP_AWP_API_URL || '')
    .replace(/\/$/, '');

export const CARVER_FACTORS = [
    'CRITICALITY',
    'ACCESSIBILITY',
    'RECUPERABILITY',
    'VULNERABILITY',
    'EFFECT',
    'RECOGNIZABILITY',
];

function authHeaders() {
    const token = localStorage.getItem('access_token');
    return token ? {'Authorization': `Bearer ${token}`} : {};
}

const EMPTY_FACTORS = CARVER_FACTORS.reduce((acc, factor) => ({...acc, [factor]: null}), {});

/**
 * CARVER matrix for a KB entity. Factors are null when unscored — never 0 — and `eligible`
 * reflects whether an admin currently allows this entity's type to be scored.
 */
export function useCarver(entityId, refetchKey = 0) {
    const [assessment, setAssessment] = useState(null);
    const [loading, setLoading] = useState(!!entityId);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!entityId) {
            setAssessment(null);
            setLoading(false);
            setError(null);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        fetch(`${BASE}/api/entities/${encodeURIComponent(entityId)}/carver`, {headers: authHeaders()})
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
            .then((data) => {
                if (cancelled) return;
                setAssessment({...data, factors: {...EMPTY_FACTORS, ...(data.factors || {})}});
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error(`${LOG_PREFIX} Error:`, err);
                setError(err);
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [entityId, refetchKey]);

    return {
        assessment,
        factors: assessment?.factors ?? EMPTY_FACTORS,
        eligible: assessment?.eligible ?? false,
        revision: assessment?.revision ?? 0,
        total: assessment?.total ?? null,
        scoredFactorCount: assessment?.scoredFactorCount ?? 0,
        loading,
        error,
    };
}

/** Newest-first CARVER factor transitions, including merge outcomes. */
export function useCarverHistory(entityId, refetchKey = 0) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(!!entityId);
    const [error, setError] = useState(null);

    const load = useCallback(() => {
        if (!entityId) {
            setEntries([]);
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);

        fetch(`${BASE}/api/entities/${encodeURIComponent(entityId)}/carver/history`, {headers: authHeaders()})
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
            .then((data) => {
                if (cancelled) return;
                setEntries(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error(`${LOG_PREFIX} History error:`, err);
                setError(err);
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [entityId]);

    useEffect(load, [load, refetchKey]);

    return {entries, loading, error};
}
